// Lunburg wishlist — guest localStorage + logged-in App Proxy sync.
// localStorage is the instant first-paint cache; the App Proxy (/apps/wishlist)
// is canonical for logged-in customers (cross-device). See projects/wishlist/PLAN.md.

const KEY = "theme:wishlist-products"; // JSON array of product GIDs
const PROXY = "/apps/wishlist";
const MERGED_FLAG = "theme:wishlist-merged"; // "1" once the guest->login union has run

// Guest sign-in nudge (shown once per page load on a guest "add").
const NUDGE_TEXT = "Saved on this device. Sign in to keep it across your devices.";
const NUDGE_LINK_LABEL = "Sign in";
let nudgeShown = false;
let nudgeTimer = null;

const state = { loggedIn: false, items: new Set(localRead()), synced: false };

function localRead() {
  try {
    const a = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(a) ? a : [];
  } catch (e) {
    return [];
  }
}
function localWrite() {
  localStorage.setItem(KEY, JSON.stringify([...state.items]));
}

function emit() {
  document.querySelectorAll("wishlist-button").forEach((el) => el.sync && el.sync());
  document.querySelectorAll("wishlist-count").forEach((el) => el.sync && el.sync());
  document.dispatchEvent(
    new CustomEvent("wishlist:change", { detail: { count: state.items.size, loggedIn: state.loggedIn } })
  );
}

async function proxy(method, body) {
  const res = await fetch(PROXY, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error("wishlist proxy " + res.status);
  return res.json();
}

// Reconcile the local cache with the server once on load.
async function hydrate() {
  try {
    const data = await proxy("GET");
    state.loggedIn = !!data.loggedIn;
    if (state.loggedIn) {
      const server = new Set(data.items || []);
      const local = localRead();
      const alreadyMerged = localStorage.getItem(MERGED_FLAG) === "1";
      const extra = local.filter((g) => !server.has(g));
      if (!alreadyMerged && extra.length) {
        // One-time guest -> login union merge: only on the FIRST authenticated
        // load after a guest session (server dedupes by GID).
        const merged = await proxy("POST", { action: "merge", ids: local });
        state.items = new Set(merged.items || []);
      } else {
        // Server is authoritative once the one-time merge has run, so a removal
        // made on another device propagates here instead of being resurrected
        // by this device's stale localStorage cache.
        state.items = server;
      }
      localStorage.setItem(MERGED_FLAG, "1");
      localWrite(); // cache server list for instant paint next time
    } else {
      // Guest: localStorage is the record. Clear the merged flag so the next
      // login performs a fresh one-time union of anything saved while guest.
      localStorage.removeItem(MERGED_FLAG);
      state.items = new Set(localRead());
    }
  } catch (e) {
    state.items = new Set(localRead()); // offline / error -> stay on cache
  }
  state.synced = true;
  emit();
}

async function toggle(gid) {
  const had = state.items.has(gid);
  // Optimistic: flip locally + paint immediately.
  if (had) state.items.delete(gid);
  else state.items.add(gid);
  localWrite();
  emit();

  if (!state.loggedIn) {
    // Guest: localStorage only. Nudge to sign in on an ADD, but only once
    // hydration has confirmed the guest state (avoids a wrong toast for a
    // logged-in visitor who clicks before hydrate resolves).
    if (state.synced && !had) showWishlistLoginToast();
    return;
  }

  try {
    const data = await proxy("POST", { action: had ? "remove" : "add", id: gid });
    state.items = new Set(data.items || []);
    localWrite();
    emit();
  } catch (e) {
    // Roll back on failure.
    if (had) state.items.add(gid);
    else state.items.delete(gid);
    localWrite();
    emit();
  }
}

// ---- Guest sign-in nudge toast (dynamic; no Liquid changes) ----
function accountLoginUrl() {
  // Reuse the header's guest login link (rendered with routes.account_login_url,
  // which resolves to the correct new-customer-accounts URL). Fallback to the
  // standard login path if the header link is not present.
  const link = document.querySelector(".header__account-dropdown-menu a.header__account-dropdown-item");
  const href = link && link.getAttribute("href");
  return href || "/account/login";
}

function ensureToast() {
  let el = document.querySelector(".wishlist-toast");
  if (el) return el;
  el = document.createElement("div");
  el.className = "wishlist-toast";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");

  const text = document.createElement("span");
  text.className = "wishlist-toast__text";
  text.textContent = NUDGE_TEXT;

  const link = document.createElement("a");
  link.className = "wishlist-toast__link";
  link.textContent = NUDGE_LINK_LABEL;
  link.setAttribute("data-no-instant", "");

  const close = document.createElement("button");
  close.type = "button";
  close.className = "wishlist-toast__close";
  close.setAttribute("aria-label", "Dismiss");
  close.textContent = "×";
  close.addEventListener("click", hideToast);

  el.appendChild(text);
  el.appendChild(link);
  el.appendChild(close);
  document.body.appendChild(el);
  return el;
}

function hideToast() {
  const el = document.querySelector(".wishlist-toast");
  if (el) el.classList.remove("is-visible");
  if (nudgeTimer) {
    clearTimeout(nudgeTimer);
    nudgeTimer = null;
  }
}

function showWishlistLoginToast() {
  if (nudgeShown) return; // once per page load
  nudgeShown = true;
  const el = ensureToast();
  const link = el.querySelector(".wishlist-toast__link");
  if (link) link.href = accountLoginUrl();
  void el.offsetWidth; // reflow so the transition runs from the hidden state
  el.classList.add("is-visible");
  if (nudgeTimer) clearTimeout(nudgeTimer);
  nudgeTimer = setTimeout(hideToast, 6000);
}

// ---- "Add all to bag": bulk-add the saved in-stock, single-variant pieces ----
// Server-side (main-wishlist.liquid) emits only purchasable single-variant ids,
// so multi-variant / sold-out / out-of-leather pieces are already excluded.
function openCartDrawer() {
  const drawer = document.querySelector("cart-drawer");
  if (drawer && typeof drawer.show === "function") {
    drawer.show();
    return;
  }
  // Fallbacks: a control that opens the drawer, else the cart link.
  const control =
    (drawer && drawer.id && document.querySelector('[aria-controls="' + drawer.id + '"]')) ||
    document.querySelector('a[href$="/cart"]');
  if (control) control.click();
}

async function wishlistAddAll(ids, btn) {
  if (!ids.length) return;
  if (btn) btn.setAttribute("aria-busy", "true");
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const res = await fetch(root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
    });
    if (!res.ok) throw new Error("cart/add " + res.status);
    // Theme-native refresh of the drawer contents + cart count + dot, then open.
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    openCartDrawer();
  } catch (e) {
    // Leave the cart unchanged on failure; no partial UI.
  } finally {
    if (btn) btn.removeAttribute("aria-busy");
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.gid = this.getAttribute("data-product-gid");
    this.btn = this.querySelector("button");
    if (this.btn && !this._wired) {
      this._wired = true;
      this.btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.gid) toggle(this.gid);
      });
    }
    this.sync();
  }
  sync() {
    if (!this.btn) return;
    const on = state.items.has(this.gid);
    this.btn.setAttribute("aria-pressed", on ? "true" : "false");
    this.btn.setAttribute("aria-label", on ? "Remove from wishlist" : "Save to wishlist");
    this.classList.toggle("is-saved", on);
    const label = this.querySelector(".wishlist-button__label");
    if (label) label.textContent = on ? "Saved" : "Save";
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.sync();
  }
  sync() {
    // Dot indicator (mirrors the cart dot): no number; CSS shows the dot when
    // items exist and hides it via .is-empty (display:none) when there are none.
    const n = state.items.size;
    this.textContent = "";
    this.classList.toggle("is-empty", n === 0);
  }
}

class WishlistAddAll extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector("button");
    if (this.btn && !this._wired) {
      this._wired = true;
      this.btn.addEventListener("click", () => {
        const ids = (this.getAttribute("data-items") || "").split(",").filter(Boolean);
        wishlistAddAll(ids, this.btn);
      });
    }
  }
}

if (!window.customElements.get("wishlist-button"))
  window.customElements.define("wishlist-button", WishlistButton);
if (!window.customElements.get("wishlist-count"))
  window.customElements.define("wishlist-count", WishlistCount);
if (!window.customElements.get("wishlist-add-all"))
  window.customElements.define("wishlist-add-all", WishlistAddAll);

hydrate();
